import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { adminSubscriptionsApi } from './sdk-client';

export class AdminSubscriptionsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminSubscriptionsError');
  }
}

/** `'pro'` remains for leftover local rows / API reads. UI filters offer Free and Champion only. */
export type AdminSubscriptionTierFilter = 'free' | 'pro' | 'champion';
export const ASSIGNABLE_ADMIN_SUBSCRIPTION_PLAN_CODES = [
  'free',
  'champion_yearly',
] as const;
export type AssignableAdminSubscriptionPlanCode =
  (typeof ASSIGNABLE_ADMIN_SUBSCRIPTION_PLAN_CODES)[number];
export type AdminSubscriptionStatusFilter =
  | 'active'
  | 'trialing'
  | 'cancelled'
  | 'expired'
  | 'grace_period'
  | 'paused'
  | 'past_due'
  | 'refunded';
export type AdminSubscriptionProviderFilter =
  | 'revenuecat'
  | 'manual'
  | 'referral_reward';

export type AdminSubscriptionsListParams = {
  tier?: AdminSubscriptionTierFilter;
  status?: AdminSubscriptionStatusFilter;
  provider?: AdminSubscriptionProviderFilter;
  expiresBefore?: string;
  limit?: number;
  offset?: number;
};

export type AdminSubscriptionListItem = {
  userId: string;
  userEmail: string | null;
  userName: string | null;
  tier: string;
  planCode: string;
  status: string;
  provider: string;
  store: string | null;
  expiresAt: string | null;
  cancelledAt: string | null;
  autoRenew: boolean;
  lastSyncedAt: string | null;
};

export type AdminSubscriptionActiveCounts = Record<string, number>;

export type AdminSubscriptionChurnMetrics = {
  windowDays: number;
  cancellationsInWindow: number;
  expirationsInWindow: number;
  renewalsInWindow: number;
  newPurchasesInWindow: number;
  activeTotal: number;
  activeByTier: Record<string, number>;
  grossChurnRate: number;
};

export type AdminSubscriptionDetailSubscription = {
  id: string;
  userId: string;
  planId: string;
  planCode: string;
  tier: string;
  status: string;
  provider: string;
  store: string | null;
  autoRenew: boolean;
  swaps: number;
  sportsLimit: number;
  swapsUsed: number;
  freeSwapUsed: boolean;
  pendingPlanCode: string | null;
  expiresAt: string | null;
  cancelledAt: string | null;
  trialEndsAt: string | null;
  pendingChangeEffectiveAt: string | null;
  currentPeriodStart: string | null;
  swapsPeriodStart: string | null;
  proOverLimitGraceEndsAt: string | null;
};

export type AdminSubscriptionPayment = {
  id: string;
  userId: string;
  subscriptionId: string | null;
  provider: string;
  providerPaymentId: string;
  amount: string;
  currency: string;
  status: string;
  createdAt: string;
};

export type AdminSubscriptionEvent = {
  id: string;
  eventType: string;
  effectiveAt: string;
  provider: string;
  reason: string | null;
  metadata: unknown;
  createdAt: string;
};

export type AdminWebhookLog = {
  id: string;
  providerEventId: string;
  eventType: string;
  appUserId: string | null;
  userId: string | null;
  rawPayload: unknown;
  receivedAt: string;
  processedAt: string | null;
  failedPermanently: boolean;
  retryCount: number;
  lastError: string | null;
};

export type AdminRevenueCatCustomer = {
  id: string;
  userId: string;
  providerCustomerId: string;
  aliases: string[];
  lastSyncedAt: string | null;
};

export type AdminSubscriptionDetail = {
  subscription: AdminSubscriptionDetailSubscription;
  recentPayments: AdminSubscriptionPayment[];
  recentEvents: AdminSubscriptionEvent[];
  recentWebhookLogs: AdminWebhookLog[];
  revenuecatCustomer: AdminRevenueCatCustomer | null;
};

export type AdminSubscriptionSyncResult = {
  outcome: string;
  userId: string;
  planCode: string | null;
  status: string | null;
  reason?: string;
};

export type AdminWebhookReprocessResult = {
  logId: string;
  outcome: string;
  eventType: string;
  providerEventId: string;
  retryCount?: number;
  error?: string;
};

export type AdminWebhookLogsParams = {
  limit?: number;
  olderThanSeconds?: number;
  eventType?: string;
  userId?: string;
  failedOnly?: boolean;
  all?: boolean;
};

export async function listAdminSubscriptions(
  params: AdminSubscriptionsListParams = {},
): Promise<AdminSubscriptionListItem[]> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerListSubscriptionsV1Raw({
        ...(params.tier ? { tier: params.tier } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.provider ? { provider: params.provider } : {}),
        ...(params.expiresBefore
          ? { expiresBefore: params.expiresBefore }
          : {}),
        ...(params.limit != null ? { limit: params.limit } : {}),
        ...(params.offset != null ? { offset: params.offset } : {}),
      }),
    fallback: 'Failed to load subscriptions',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function getAdminSubscriptionActiveCounts(): Promise<AdminSubscriptionActiveCounts> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerGetActiveCountsV1Raw(),
    fallback: 'Failed to load active subscription counts',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function getAdminSubscriptionChurn(
  windowDays?: number,
): Promise<AdminSubscriptionChurnMetrics> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerGetChurnMetricsV1Raw({
        ...(windowDays != null ? { windowDays } : {}),
      }),
    fallback: 'Failed to load churn metrics',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function getAdminSubscriptionDetail(
  userId: string,
): Promise<AdminSubscriptionDetail> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerGetDetailV1Raw({
        userId,
      }),
    fallback: 'Failed to load subscription detail',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function syncAdminSubscription(
  userId: string,
): Promise<AdminSubscriptionSyncResult> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerForceSyncV1Raw({
        userId,
      }),
    fallback: 'Failed to sync subscription',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function listAdminWebhookLogs(
  params: AdminWebhookLogsParams = {},
): Promise<AdminWebhookLog[]> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerListWebhookLogsV1Raw({
        ...(params.limit != null ? { limit: params.limit } : {}),
        ...(params.olderThanSeconds != null
          ? { olderThanSeconds: params.olderThanSeconds }
          : {}),
        ...(params.eventType ? { eventType: params.eventType } : {}),
        ...(params.userId ? { userId: params.userId } : {}),
        ...(params.failedOnly != null ? { failedOnly: params.failedOnly } : {}),
        ...(params.all != null ? { all: params.all } : {}),
      }),
    fallback: 'Failed to load webhook logs',
    ErrorClass: AdminSubscriptionsError,
  });
}

export async function reprocessAdminWebhookLog(
  id: string,
): Promise<AdminWebhookReprocessResult> {
  return executeSdkRawRequest({
    request: () =>
      adminSubscriptionsApi.adminSubscriptionsControllerReprocessWebhookLogV1Raw(
        { id },
      ),
    fallback: 'Failed to reprocess webhook log',
    ErrorClass: AdminSubscriptionsError,
  });
}
